import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "Constant/RouteUrls";
import { setAlertData, setRightHead } from "Redux/Screen/Action";
import InsuranceApi from "Service/Insurance.api";
import PracticeApi from "Service/Practice.api";
import CommonFormatter from "Utils/Common.Formatter";
import Format from "Pages/Home/HeadScreen/OrgMgmt/List/Format";
import { PrgmListResType } from "DataTypes/Services/Prgm.type";
import { FormatOrgListType } from "DataTypes/PracticeMangement.type";
import PracticeListingSection from "./PracticeListingSection";
import ProgramInsuranceSection from "./ProgramInsuranceSection";
import {

  PracticeInsuranceRow,
} from "./types";
import {
  buildSavePayloads,
  getKey,
  insuranceName,
  mergeProgramKeys,
  normalizeInsuranceRows,
  normalizeMappings,
  syncSavedInitialKeys,
} from "./Format";

const PracticeInsuranceMaster: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [practiceLoading, setPracticeLoading] = useState(true);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [practiceSearch, setPracticeSearch] = useState("");
  const [insuranceSearch, setInsuranceSearch] = useState("");
  const [practices, setPractices] = useState<FormatOrgListType[]>([]);
  const [selectedPractice, setSelectedPractice] =
    useState<FormatOrgListType | null>(null);
  const programs: PrgmListResType[] = selectedPractice?.prgmArr || [];
  const [insuranceRows, setInsuranceRows] = useState<PracticeInsuranceRow[]>(
    []
  );
  const [insurancePracticeId, setInsurancePracticeId] = useState<number | null>(
    null
  );
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [initialCheckedKeys, setInitialCheckedKeys] = useState<Set<string>>(
    new Set()
  );
  const [dirtyProgramIds, setDirtyProgramIds] = useState<Set<number>>(
    new Set()
  );
  const [loadedProgramIds, setLoadedProgramIds] = useState<Set<number>>(
    new Set()
  );
  const [selectedProgramAbbr, setSelectedProgramAbbr] = useState("CCM");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  const filteredPractices = useMemo(() => {
    const search = practiceSearch.trim().toLowerCase();
    if (!search) return practices;
    return practices.filter((practice) =>
      practice.name.toLowerCase().includes(search)
    );
  }, [practiceSearch, practices]);

  const hasChanges = useMemo(() => {
    return dirtyProgramIds.size > 0;
  }, [dirtyProgramIds]);

  const filteredInsuranceRows = useMemo(() => {
    const search = insuranceSearch.trim().toLowerCase();
    if (!search) return insuranceRows;
    return insuranceRows.filter((insurance) =>
      insuranceName(insurance).toLowerCase().includes(search)
    );
  }, [insuranceRows, insuranceSearch]);

  const selectedProgram = useMemo(() => {
    return (
      programs.find(
        (program) => program.abbrevation == selectedProgramAbbr
      ) ||
      programs.find((program) => program.id) ||
      programs[0]
    );
  }, [programs, selectedProgramAbbr]);

  useEffect(() => {
    dispatch(setRightHead("practice-insurance-master"));
    CommonFormatter.HandleNavArr({
      id: "practice-insurance-master",
      label: "Practice Insurance Master",
      path:
        `/${RouteUrls.hs}/${RouteUrls.master}/` +
        `${RouteUrls.practiceInsMaster}`,
      level: 1,
      link: RouteUrls.practiceInsMaster,
      navType: "master",
    });
    loadPractices();
    return () => {
      dispatch(setRightHead(""));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!selectedPractice) return;
    setSelectAllChecked(false);
    loadInsuranceMapping(selectedPractice);
  }, [selectedPractice]);

  useEffect(() => {
    if (!selectedPractice || !selectedProgram?.id) return;
    const practiceId = selectedPractice.practiceId || selectedPractice.id;
    if (insurancePracticeId != practiceId) return;
    loadProgramMapping(selectedPractice, selectedProgram);
  }, [insurancePracticeId, selectedPractice, selectedProgram]);

  useEffect(() => {
    if (!selectedProgram?.id || filteredInsuranceRows.length == 0) {
      setSelectAllChecked(false);
      return;
    }
    const allVisibleRowsChecked = filteredInsuranceRows.every((insurance) =>
      checkedKeys.has(getKey(insurance, selectedProgram))
    );
    setSelectAllChecked(allVisibleRowsChecked);
  }, [checkedKeys, filteredInsuranceRows, selectedProgram]);

  const loadPractices: () => Promise<void> = async () => {
    setPracticeLoading(true);
    const res: any = await PracticeApi.list({
      data: { isDeleted: null },
      offset: 0,
      limit: 500,
    });
    if (res?.success) {
      const formatted = Format.FormatListResponse(res?.data);
      const filtered = formatted.filter((practice) => !practice.masterPractice);
      setPractices(filtered);
      setSelectedPractice((current) => current || filtered[0] || null);
      loadPracticeInsuranceCounts(filtered);
    }
    setPracticeLoading(false);
  };

  const loadPracticeInsuranceCounts: (
    practiceList: FormatOrgListType[]
  ) => Promise<void> = async (practiceList) => {
    const batchSize = 8;
    for (let index = 0; index < practiceList.length; index += batchSize) {
      const batch = practiceList.slice(index, index + batchSize);
      const counts = await Promise.all(
        batch.map(async (practice) => {
          const practiceId = practice.practiceId || practice.id;
          if (!practiceId) return null;
          const res: any = await InsuranceApi.list({
            data: { practiceId },
          });
          if (!res?.success) return null;
          return {
            id: practice.id,
            count: normalizeInsuranceRows(res?.data, practiceId).length,
          };
        })
      );
      const countByPractice = new Map(
        counts
          .filter(Boolean)
          .map((item) => [item?.id, item?.count])
      );
      setPractices((current) =>
        current.map((practice) =>
          countByPractice.has(practice.id)
            ? {
              ...practice,
              insuranceCount: countByPractice.get(practice.id) || 0,
            }
            : practice
        )
      );
    }
  };

  const loadInsuranceMapping: (
    practice: FormatOrgListType
  ) => Promise<void> = async (practice) => {
    const practiceId = practice.practiceId || practice.id;
    if (!practiceId || programs.length == 0) return;
    setMappingLoading(true);
    setInsurancePracticeId(null);
    const searchRes: any = await InsuranceApi.list({
      data: { practiceId },
    });

    if (searchRes?.success) {
      const rows = normalizeInsuranceRows(searchRes?.data, practiceId);
      setInsuranceRows(rows);
      setCheckedKeys(new Set());
      setInitialCheckedKeys(new Set());
      setDirtyProgramIds(new Set());
      setLoadedProgramIds(new Set());
      setPractices((current) =>
        current.map((item) =>
          item.id == practice.id
            ? { ...item, insuranceCount: rows.length }
            : item
        )
      );
      await loadAllProgramMappings(practiceId, programs);
      setInsurancePracticeId(practiceId);
    }
    setMappingLoading(false);
  };

  const loadAllProgramMappings: (
    practiceId: number,
    columns: PrgmListResType[]
  ) => Promise<void> = async (practiceId, columns) => {
    const activePrograms = columns.filter((program) => program.id);
    if (activePrograms.length == 0) return;

    const responses = await Promise.all(
      activePrograms.map(async (program) => {
        const programId = Number(program.id);
        const res: any = await InsuranceApi.read({
          data: {
            practiceId,
            programId,
          },
        });
        if (!res?.success) return null;
        const programKeys = normalizeMappings(
          res?.data,
          practiceId,
          programId
        ).map((mapping) =>
          `${mapping.companyId}_${mapping.planId}_${mapping.programId}`
        );
        return { programId, programKeys };
      })
    );

    let nextKeys = new Set<string>();
    const nextLoadedProgramIds = new Set<number>();
    responses.filter(Boolean).forEach((item) => {
      const programMapping = item as {
        programId: number;
        programKeys: string[];
      };
      nextKeys = mergeProgramKeys(
        nextKeys,
        programMapping.programId,
        programMapping.programKeys
      );
      nextLoadedProgramIds.add(programMapping.programId);
    });

    setCheckedKeys(nextKeys);
    setInitialCheckedKeys(new Set(nextKeys));
    setLoadedProgramIds(nextLoadedProgramIds);
  };

  const loadProgramMapping: (
    practice: FormatOrgListType,
    program: PrgmListResType,
    force?: boolean
  ) => Promise<void> = async (practice, program, force) => {
    const practiceId = practice.practiceId || practice.id;
    const programId = Number(program.id || 0);
    if (
      !practiceId ||
      !programId ||
      (!force && loadedProgramIds.has(programId))
    ) return;
    setMappingLoading(true);
    const res: any = await InsuranceApi.read({
      data: {
        practiceId,
        programId,
      },
    });
    if (res?.success) {
      const programKeys = normalizeMappings(
        res?.data,
        practiceId,
        programId
      ).map((mapping) =>
        `${mapping.companyId}_${mapping.planId}_${mapping.programId}`
      );
      setCheckedKeys((current) =>
        mergeProgramKeys(current, programId, programKeys)
      );
      setInitialCheckedKeys((current) =>
        mergeProgramKeys(current, programId, programKeys)
      );
      setLoadedProgramIds((current) =>
        new Set(current).add(programId)
      );
    }
    setMappingLoading(false);
  };

  const handleCheck: (
    insurance: PracticeInsuranceRow,
    program: PrgmListResType
  ) => void = (insurance, program) => {
    if (!program.id) return;
    const key = getKey(insurance, program);
    const nextKeys = new Set(checkedKeys);
    if (nextKeys.has(key)) nextKeys.delete(key);
    else nextKeys.add(key);
    setDirtyProgramIds((current) => new Set(current).add(Number(program.id)));
    setCheckedKeys(nextKeys);
  };

  const handleSelectAll: () => void = () => {
    if (!selectedProgram?.id) return;
    const nextKeys = new Set(checkedKeys);
    filteredInsuranceRows.forEach((insurance) => {
      const key = getKey(insurance, selectedProgram);
      if (selectAllChecked) nextKeys.delete(key);
      else nextKeys.add(key);
    });
    setSelectAllChecked(!selectAllChecked);
    setDirtyProgramIds((current) =>
      new Set(current).add(Number(selectedProgram.id))
    );
    setCheckedKeys(nextKeys);
  };

  const handleSave: () => Promise<void> = async () => {
    if (!selectedPractice) return;
    const practiceId = selectedPractice.practiceId || selectedPractice.id;
    if (!practiceId) return;
    setSaving(true);
    const payloads = buildSavePayloads(
      checkedKeys,
      insuranceRows,
      programs,
      practiceId,
      dirtyProgramIds
    );
    if (payloads.length == 0) {
      setSaving(false);
      return;
    }
    const responses = await Promise.all(
      payloads.map((payload) => InsuranceApi.saveUpdate({ data: payload }))
    );
    setSaving(false);
    if (responses.every((res: any) => res?.success)) {
      dispatch(
        setAlertData({
          alertMessage: "Program Insurance Mapped Successfully.",
          openAlert: true,
          alertVariant: "success",
        })
      );
      setInitialCheckedKeys(
        syncSavedInitialKeys(
          checkedKeys,
          initialCheckedKeys,
          dirtyProgramIds
        )
      );
      setDirtyProgramIds(new Set());
      setPractices((current) =>
        current.map((item) =>
          item.id == selectedPractice.id
            ? { ...item, insuranceCount: insuranceRows.length }
            : item
        )
      );
    }
  };

  return (
    <Fragment>
      <Box sx={{ ...sxPageScale }}>
        <Box sx={{ ...sxMain(sidebarCollapsed) }}>
          <PracticeListingSection
            collapsed={sidebarCollapsed}
            onToggleCollapsed={() => setSidebarCollapsed(!sidebarCollapsed)}
            practiceSearch={practiceSearch}
            onPracticeSearchChange={setPracticeSearch}
            loading={practiceLoading}
            practices={filteredPractices}
            selectedPractice={selectedPractice}
            onSelectPractice={setSelectedPractice}
            insuranceCount={insuranceRows.length}
            onAddPractice={() =>
              navigate(
                `/${RouteUrls.hs}/${RouteUrls.organization}/${RouteUrls.add}`
              )
            }
          />
          <ProgramInsuranceSection
            selectedPractice={selectedPractice}
            insuranceRows={insuranceRows}
            programColumns={programs}
            selectedProgramAbbr={selectedProgramAbbr}
            onSelectProgramAbbr={setSelectedProgramAbbr}
            selectedProgram={selectedProgram}
            insuranceSearch={insuranceSearch}
            onInsuranceSearchChange={setInsuranceSearch}
            selectAllChecked={selectAllChecked}
            onSelectAll={handleSelectAll}
            mappingLoading={mappingLoading}
            saving={saving}
            hasChanges={hasChanges}
            filteredInsuranceRows={filteredInsuranceRows}
            checkedKeys={checkedKeys}
            onCheck={handleCheck}
            onSave={handleSave}
          />
        </Box>
      </Box>
    </Fragment>
  );
};

const sxPageScale = {
  height: "100%",
  width: "100%",
  display: "flex",
  flexDirection: "column",
};

const sxMain = (collapsed: boolean) => ({
  display: "grid",
  gridTemplateColumns: collapsed
    ? "58px minmax(0, 1fr)"
    : "286px minmax(0, 1fr)",
  gap: 0.75,
  minHeight: 0,
  flex: 1,
  transition: "grid-template-columns 0.2s ease",
});

export default PracticeInsuranceMaster;
